import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Mail, ChevronRight, X, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { messagesAPI } from '../../lib/api';

const MessagesTab = ({ messages, user, onMarkRead, onRefresh }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const handleMarkMessageRead = async (messageId) => {
    try {
      await messagesAPI.markRead(messageId);
      onMarkRead?.(messageId);
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedMessage) return;
    
    setSendingReply(true);
    try {
      await messagesAPI.reply(selectedMessage.id, { content: replyContent });
      toast.success('Reply sent successfully!');
      setReplyContent('');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Message List */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-white">Inbox</h3>
        {messages.length === 0 ? (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="p-8 text-center">
              <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No messages yet</p>
            </CardContent>
          </Card>
        ) : (
          messages.map(message => {
            const isRead = message.read_by?.includes(user?.id);
            return (
              <Card 
                key={message.id} 
                className={`cursor-pointer transition-all bg-[#111] border-[#333] ${!isRead ? 'border-l-4 border-l-[#7000FF] bg-[#7000FF]/10' : ''} ${selectedMessage?.id === message.id ? 'ring-2 ring-[#7000FF]' : 'hover:border-[#7000FF]/50'}`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!isRead) handleMarkMessageRead(message.id);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {!isRead && <div className="w-2 h-2 bg-[#7000FF] rounded-full" />}
                        <h4 className={`font-semibold truncate ${!isRead ? 'text-white' : 'text-white/80'}`}>
                          {message.subject}
                        </h4>
                      </div>
                      <p className="text-sm text-white/50 truncate">{message.content}</p>
                      <p className="text-xs text-white/30 mt-1">
                        From: {message.sender_name} • {new Date(message.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/30 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Message Detail & Reply */}
      <div>
        {selectedMessage ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{selectedMessage.subject}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-neutral-500">
                From: {selectedMessage.sender_name} • {new Date(selectedMessage.created_at).toLocaleString()}
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap bg-neutral-50 p-4 rounded-lg">
                {selectedMessage.content}
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Reply to UPA
                </h4>
                <Textarea
                  placeholder="Type your reply here..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={4}
                  className="mb-3"
                />
                <Button 
                  onClick={handleSendReply} 
                  disabled={!replyContent.trim() || sendingReply}
                  className="w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingReply ? 'Sending...' : 'Send Reply'}
                </Button>
                <p className="text-xs text-neutral-500 mt-2 text-center">
                  Your reply will be sent to theteam@upaschool.com
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="h-64 flex items-center justify-center">
            <div className="text-center text-neutral-500">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Select a message to read</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MessagesTab;
